import _ from 'lodash';
import moment from 'moment';
import {
    OVERVIEW_PANEL_CHANGE_CURRENT,
    OVERVIEW_PANEL_GET_CURRENT,
    OVERVIEW_PANEL_IS_VISIBLE
} from '../../store/modules/overview-panel/types';
import api from '../api';
import translate from '../core/tools/translate';
import environment from '../environment';
import { CoreControl } from './CoreControl';
import { VectorType } from './VectorType';
import filters from '../core/filters';
import store from '../../store';
import worldMap from '../core/map';
import dataBoard from '../core/databoard';
import DataTable from '../core/charts/Datatable';
import numeral from 'numeral';
import type { Period } from './types/control-period.type';
import Workspace from '../workspaces';
import { ControlType, Item } from '../workspaces/item';
import { ContextEnum } from '../../enuns/context.enum';
import { FeaturePermission } from '../../constants/feature.permission.helper';

const LayerInfo = (name) => require('../info').get(name);
// const LayerControls = (name) => require('../controls').get(name);
const get = (name, currentMap = worldMap.getCurrent()) => {
    return require('../layers/index').default.get(name, currentMap);
};

const isAuthorized = (permission, feature) => api.authorization.isAuthorized(permission, feature);

export class ClusterSalesControl extends CoreControl {
    period: Period;
    constructor () {
        const options = {
            id: 'clusterSales',
            name: 'ClusterSales',
            filter: true,
            overview: true,
            request: api.themes.get.clusterSales
        };

        super(options);

        this._changePeriod = this._changePeriod.bind(this);
        this._change = this._change.bind(this);
        this.toggleOverview = this.toggleOverview.bind(this);
        this.createFilter = this.createFilter.bind(this);
        this._filterCallback = this._filterCallback.bind(this);
        this.reload = this.reload.bind(this);
        this.colorize = this.colorize.bind(this);
        this._getLayerOptions = this._getLayerOptions.bind(this);
        this.initPeriod();
        this.init();
    }

    toggleOverview() {
        const currentMap = worldMap.getCurrent();
        const layer = this.getLayer(currentMap._id);
        const features = layer.getCurrentFeatures();

        if (!store.getters[OVERVIEW_PANEL_IS_VISIBLE] || !_.isEqual(this._overviewFeatures, features))
            api.general.log.action(ContextEnum.LayerClusterSales, 'Visualizar Gráfico');

        if (_.size(features)) {
            this.summary(features);
            this._overviewFeatures = _.cloneDeep(features);
            store.dispatch(OVERVIEW_PANEL_CHANGE_CURRENT, this.options.name);
        }
    }
    static getInstance () {
        if (!ClusterSalesControl._instance) {
            ClusterSalesControl._instance = new ClusterSalesControl();
        }

        return ClusterSalesControl._instance;
    }

    init () {
        this.options.id = 'layer-cluster-sales';
        const values = {
            help: translate(`Help.${this.options.name}`),
            id: 'layer-cluster-sales',
            input: 'checkbox',
            label: translate('Common.ClusterSales'),
            layer: this.getLayer,
            change: this._change,
            name: this.options.name,
            type: 'vector-cluster-heat',
            filter: this.options.filter ? this.createFilter : null,
            overview: this.options.overview ? this.toggleOverview : null,
            links: {}
        };

        const formatDate = (date: Date): string => moment(date).format('MMM/YYYY');
        const {start, end} = this.period;
        values.links[translate('Action.ShowDateFrom')] = {
            id: 'layer-cluster-sales-date',
            name: 'ClusterSalesByYear',
            text: `${formatDate(start)} -> ${formatDate(end)}`,
            value: this.valueForDatePicker,
            change: this._changePeriod,
            type: 'datepicker'
        };

        if (!this._instance) {
            this._instance = new VectorType(values);
        }
    }

    get valueForDatePicker(){
        const years = environment.get('CONFIG.FILTERS.AVERAGE_SALES');
        return {
            ...this.period,
            max: environment.get('CONFIG.FILTERS.ENDYEAR'),
            min: new Date(years[years.length - 1],0,1),
        };
    }

    initPeriod(){
        const end = environment.get('CONFIG.FILTERS.ENDYEAR');
        const start = new Date(end.getFullYear(), 0, 1);
        this.period = { start, end };
    }

    get dateApiParams(){
        const adjustPeriod = (dt) => {
            const initialDate = dt, finalDate = new Date(dt.getTime()), month = finalDate.getMonth();
            return finalDate.getMonth() !== month ? finalDate : initialDate;
        };
        const apiDateTime = (date: Date) => moment(date).format("YYYY/MM/DD");
        const {start, end} = this.period;
        return {
            initialDate: apiDateTime(start),
            finalDate: apiDateTime(adjustPeriod(end))
        };
    }

    _changePeriod (data) {
        this.period = data;
        api.general.log.action(ContextEnum.LayerClusterSales, 'Mudou Período', JSON.stringify(this.period));
        this.getLayer().setOptions({
            remote: {
                params: {
                    bandeiras: this.getLayer().options.remote.params.bandeiras,
                    ...this.dateApiParams,
                    bbox: 'LINESTRING(-13.095703125 5.266007882805511, -86.923828125 -33.46810795527895)'
                }
            }
        });

        if (this.check()) {
            this.getLayer().redraw();
        }
    }

    _change (e) {
        Workspace.addAction(new Item(`#${$(e).prop('id')}`, e.checked, ControlType.ICheckBox));
        api.general.log.action(ContextEnum.LayerClusterSales, e.checked ? 'enable' : 'disable');

        const layer = this.getLayer();
        if (e.checked) {
            if (!layer.isEmpty() && layer.samePosition()) {
                layer.reload();
            } else {
                layer.redraw();
            }
            this._setFilterButtonVisibility(true);
        } else {
            layer.hide();
            this._setFilterButtonVisibility(false);
        }
    }

    getType () {
        const { params } = this.getLayer().options.remote;
        return params.locationType;
    }

    getTypeValue () {
        return this.getType();
    }

    getDate () {
        return this.period;
    }

    _setFilterButtonVisibility(state: boolean) {
        if (this.options.filter) {
            try {
                this.getControls().button.filter.input.enable(state);
                this.getControls().button.filter.enable = state;

                if (state) {
                    this.getControls().slider.range.input.enable(true);
                    this.getControls().slider.range.enable = true;
                    this.getControls().slider.range.input.value(3);
                    this.getControls().slider.range.value = 3;

                    this.createFilter();
                }

                if (!state) {
                    filters.close();
                }
            } catch (err) {
                console.log('ERROR: ', err);
            }
        }

        try {
            this.getControls().button.overview.input.enable(state);
            this.getControls().button.overview.enable = state;
            if (store.getters[OVERVIEW_PANEL_GET_CURRENT] === this.options.name && !state) {
                dataBoard.hide();
                delete this._overviewFeatures;
            }
        } catch (err) {
            console.log(err);
        }
    }

    createFilter() {
        filters.create({
            container: document.getElementById('filter-container'),
            id: `filter-${_.kebabCase(this.options.name)}-${this.getLayer().getMap()._id}`,
            label: translate(`Common.${this.options.name}`),
            type: 'clusterSales',
            direction: 'up',
            onSubmit: this._filterCallback,
            fields: {
                segments: true,
                brands: true,
                groups: true,
                fuelTypes: true,
            }
        });
    }

    _filterCallback() {
        if (this.check() && this.getLayer().visible()) {
            this.reload();
        }
    }

    reload() {
        if (this.check() && this.getLayer().visible()) {
            const layer = this.getLayer();
            this._cachePages = new Map();
            this.colorize(layer.toGeoJSON(), layer).then(res => layer.setFeatures(res, true));
        }
    }

    colorize(featureCollection: any, layer: any = this.getLayer()) {
        return new Promise((resolve, reject) => {
            const id = layer.getMap()._id;

            this.options
                .request(this._getLayerOptions(id))
                .then(res => {
                    if (!_.size(res) || !_.some(res, r => r.Valor))
                        resolve(this._setFeaturesValue(res));

                    this._setFilterButtonVisibility(true);
                });
        });
    }

    _setFeaturesValue(featureCollection: any) {
        if (!featureCollection.objects.output) {
            return null;
        }

        let result = {
            type: 'FeatureCollection',
            features: featureCollection.objects.output.geometries.map(g => ({
                type: 'Feature',
                properties: g.properties,
                geometry: {
                    type: g.type,
                    coordinates: g.coordinates
                }
            }))
        };
        return result;
    }

    _getLayerOptions(id: number) {
        const filterData = filters.get(`filter-${_.kebabCase(this.options.name)}-${this.getLayer().getMap()._id}`, id) || {};
        const options = {
            bandeiras:  (() => {
                let flags = [];

                _.forEach(environment.get('CONFIG.DEALERS'), flag => flags.push(flag));
                _.forEach(environment.get('CONFIG.COMPETITORS'), flag => flags.push(flag));

                return flags.join(',');
            })(),
            ...this.dateApiParams,
            bbox: 'LINESTRING(-13.095703125 5.266007882805511, -86.923828125 -33.46810795527895)',
        };

        if (filterData.models) {
            options.idSegmentos = filterData.models.value;
        } else if (filterData.competingClassification && _.size(filterData.competingClassification.value)) {
            options.idSegmentos = _.flatMap(filterData.competingClassification.value, key => _.map(environment.get('CONFIG.FILTERS.COMPETING_SEGMENTS')[key], 'id'));
        }

        if (filterData.brands) {
            options.bandeiras = filterData.brands.text.join(',');
        }

        if (filterData.group) {
            options.idGrupos = filterData.group.value;
        }

        if (filterData.fuelTypes) {
            options.idFuelTypes = filterData.fuelTypes.value;
        }

        if (filterData.industry && filterData.industry.value) {
            options.idIndustry = _.isArray(filterData.industry.value)
                ? filterData.industry.value.map(x => x.toString())
                : [filterData.industry.value.toString()];
        }

        this.getLayer().setOptions({
            remote: {
                params: options
            }
        });

        return options;
    }

    _calculateSalesAverage(values: any[]){
        const {end, start} = this.period;
        const monthDiff = (d1, d2) => {
            let months = (d2.getFullYear() - d1.getFullYear()) * 12;
            months -= d1.getMonth() + 1;
            months += d2.getMonth() + 2;
            return months > 0 ? months : 1;
        };
        return _.sumBy(values, 'Total') /
           monthDiff(start, end);
    }

    calculate(features) {
        let summary = _(features)
            .filter(f => f.properties)
            .map(f => f.properties)
            .flatten()
            .filter(f => f.Total > 0)
            .groupBy(f => `${f.Brand}, ${f.City}, ${f.State}`)
            .map((values, key) => {
                const crrValue = values.findOrFirst(x => true);
                return {
                    name: !!crrValue ? crrValue.Brand : '',
                    city: !!crrValue ? crrValue.City : '',
                    state: !!crrValue ? crrValue.State : '',
                    totalStores: values.length,
                    total: _.sumBy(values, 'Total'),
                    average: this._calculateSalesAverage(values)
                };
            })
            .orderBy(f=> f.total)
            .reverse()
            .map(f=> f)
            .value();
        let detailsBrands = _(features)
            .filter(f => f.properties)
            .map(f => f.properties)
            .flatten()
            .filter(f => f.Total > 0)
            .groupBy(f => f.Brand)
            .map((f, key) => {
                return {
                    name: key,
                    total: _.sumBy(f, 'Total')
                };
            })
            .orderBy(f=> f.total)
            .reverse()
            .map(f=> f)
            .value();

        let totalRegistration = _.sumBy(detailsBrands, 'total');
        const perPercentage =  _(detailsBrands)
            .orderBy(f => f.total)
            .reverse()
            .map(f => ({
                [translate('Common.Name')]: f.name,

                [translate('Common.Total')]: f.total / totalRegistration
            }))
            .value();

        const dataCharColumn = _(detailsBrands)
            .orderBy(f => f.total)
            .reverse()
            .map(f => ({
                [translate('Common.Name')]: f.name,
                [translate('Common.Total')]: f.total
            }))
            .value();

        const detailBySegments = _(features)
            .filter(f => f.properties)
            .map(f => f.properties.Segmentos)
            .flatten()
            .filter(f => f.Id > 0 && f.Total > 0)
            .groupBy(f => f.Id)
            .map((f, key) => {
                return {
                    idSegment: key,
                    nameSegment: f[0].Nome || '',
                    value: _.sumBy(f, 'Total'),
                };
            })
            .value();

        const totalSegments = _.sumBy(detailBySegments, 'value');
        const perSegments = _(detailBySegments)
            .orderBy(f => f.value)
            .reverse()
            .map(f => ({
                [translate('Common.Name')]: f.nameSegment,

                [translate('Common.Total')]: f.value / totalSegments
            }))
            .value();

        return {
            summary,
            dataCharColumn,
            perPercentage,
            perSegments
        };
    }

    summary(features) {
        const data = this.calculate(features);

        const chartColumn = new DataTable(data.dataCharColumn, {
            row: translate('Common.Name'),
            column: [translate('Common.Total')]
        });

        const chartPie = new DataTable(data.perPercentage, {
            row: translate('Common.Name'),
            column: [translate('Common.Total')]
        });

        const chartPieSecond = new DataTable(data.perSegments, {
            row: translate('Common.Name'),
            column: [translate('Common.Total')]
        });

        const options = {
            title: {
                table: `${translate(`Common.TableData`)}`,
                chartPie: `${translate(`Common.RankingSalesBrands`)}`,
                chartColumn: `${translate(`Common.RankingSalesBrandsUnits`)}`,
                chartPieSecond: `RANKING ${translate(`Common.Segments`)}`
            },
            size: '40%',
            dash: true,
            fourthLevel: (_.size(data.perSegments) > 0),
            titleHeader: true,
            nameFile: `${environment.get('CONFIG.CLIENT')}_Painel de Dados_${translate(`Common.${this.options.name}`)}_v${moment().format('YYYYMMDD')}`,
            limit: 9,//_.size(data.summary),
            titleHeaderMessage: `Sumário - ${translate(`Common.${this.options.name}`)}  | ${this.formattedPeriod()}`,
            data: [data.summary, chartPie, chartColumn, chartPieSecond],
            legends:{
                dash1: {
                    // visible: false,
                    // position: 'right',
                    // labels: {
                    //     font: `10px 'Roboto Condensed', sans-serif`
                    // }
                }
            },
            format: [
                '{0:n0}',
                '{0:p1}',
                '{0:n0}'
            ],
            convertTable: false,
            columns: [
                {
                    field: 'name',
                    title: translate('Common.Brand')
                },
                {
                    field: 'city',
                    title: translate('Common.County')
                },
                {
                    field: 'state',
                    title: translate('Common.State')
                },
                {
                    field: 'totalStores',
                    title: translate('Common.QttStores')
                },
                {
                    field: 'average',
                    title: translate('Common.AverageSales'),
                    template: data => numeral(data.average).format("0,0[.]00")
                },
                {
                    field: 'total',
                    title: translate('Common.Total'),
                    width: 100,
                    template: data => numeral(data.total).format("0,0[.]00")
                }
            ],
            buttons:{
                fullscreen: true,
                export: isAuthorized(FeaturePermission.LAYER_CLUSTER_SALES.exportDataPanel)
            }
        };
        dataBoard.show(options);
    }
}

export default ClusterSalesControl;



// WEBPACK FOOTER //
// ./src/scripts/controls/ClusterSalesControl.js